/**
 * Latency utilities — timeout wrapper, graph build exclusion, degrade policy.
 *
 * Mirrors the Promise-sharing pattern from analyzer-bridge.ts for concurrent
 * graph build exclusion.
 */

// ---------------------------------------------------------------------------
// Timeout wrapper
// ---------------------------------------------------------------------------

export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`Operation timed out after ${ms}ms`);
    this.name = "TimeoutError";
  }
}

/**
 * Race a promise against a timeout. Resolves with the promise result or
 * rejects with TimeoutError.
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<T> {
  if (timeoutMs <= 0) return promise;

  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(timeoutMs)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer!);
  }
}

// ---------------------------------------------------------------------------
// Exclusive lock (Promise sharing)
// ---------------------------------------------------------------------------

/**
 * Creates an exclusive async lock that ensures only one invocation runs at a
 * time. Subsequent callers await the same Promise (analyzer-bridge pattern).
 */
export function createExclusiveLock<T>(): {
  run: (fn: () => Promise<T>) => Promise<T>;
} {
  let pending: Promise<T> | null = null;

  return {
    async run(fn: () => Promise<T>): Promise<T> {
      if (pending) return pending;
      pending = fn().finally(() => {
        pending = null;
      });
      return pending;
    },
  };
}

// ---------------------------------------------------------------------------
// Degrade policy
// ---------------------------------------------------------------------------

export interface DegradeResult<T> {
  value: T;
  degraded: boolean;
  reason?: string;
}

/**
 * Attempt an operation with timeout; if it fails or times out, return the
 * fallback value with `degraded: true`.
 */
export async function withDegrade<T>(
  operation: () => Promise<T>,
  fallback: T,
  timeoutMs: number,
): Promise<DegradeResult<T>> {
  try {
    const value = await withTimeout(operation(), timeoutMs);
    return { value, degraded: false };
  } catch (err) {
    const reason =
      err instanceof TimeoutError
        ? err.message
        : err instanceof Error
          ? err.message
          : String(err);
    return { value: fallback, degraded: true, reason };
  }
}
